import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Form, Formik, useFormikContext} from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import {useIntl} from 'react-intl';
import {useLocation, useNavigate} from 'react-router-dom';
import {Box} from '@mui/material';

import HttpClient from '../../../api/api';
import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {getAllEmployees} from '../../../features/employees/short';
import {getAllAbsencesTypes, getAllShiftsTypes, getAllTagsTypes} from '../../../features/types';
import {getEmployee} from '../../../features/employees';
import {createShift} from '../../../features/shifts';
import {Shift} from '../../../features/shifts/types';
import {createAbsence} from '../../../features/absences';
import {Absence} from '../../../features/absences/types';
import {Employee} from '../../../features/employees/types';
import {AbsenceBehavior, TiminatorAbsenceType, TiminatorTagType, TiminatorType} from '../../../features/types/types';
import {Day} from '../../../features/settings/workcalendar/types';
import {NAVIGATION_ROUTES} from '../../../routes/NavigationRoute';
import {Lx} from '../../../i18n/consts';
import BaseForm from '../../../components/shared/form-controls/base-form';
import { FormCheckbox } from '../../../components/shared/form-controls/form-checkbox';
import {FormTextarea} from '../../../components/shared/nextgen/form/form-textarea';
import FormDateSelector from '../../../components/shared/nextgen/form/form-date-selector';
import { FormNumberInput } from '../../../components/shared/nextgen/form/form-number-input';
import {
    canManageOldTimesheetItems,
    canManageTimesheetInternalNote,
    canManageTimesheetItems,
    canSeeTimesheetPage
} from '../../../features/profile/entityPermissions';
import { REmployees } from '../../../features/roles/types';
import { PIdNameModel } from '../../../features/projects/types';
import { DropdownField } from '../../../components/shared/nextgen/dropdown-field';
import FormAutocomplete from '../../../components/shared/form-controls/form-autocomplete';

interface CopyItemPrefill {
    kind: 'shift' | 'absence';
    typeId: number;
    hours: number;
    note?: string;
    internalNote?: string;
    isOvertime?: boolean;
    tags?: PIdNameModel[];
}

interface CreateShiftLocationState {
    employeeId?: number;
    date: string;
    lockEmployee?: boolean;
    projectId?: number;
    projectName?: string;
    projects?: PIdNameModel[];
    copy?: CopyItemPrefill;
    from?: string;
}

type TimeShiftTypeDropdownItem = {
    id: string;
    name: string;
    kind: 'shift' | 'absence';
    type: TiminatorType | TiminatorAbsenceType;
};

interface CreateForm {
    employee: REmployees | null;
    date: string;
    shiftType: TimeShiftTypeDropdownItem | null;
    project: PIdNameModel | null;
    tags: PIdNameModel[];
    hours: number;
    plannedHours: number | null;
    isOvertime: boolean;
    putIntoTimeBank: boolean;
    takeFromTimeBank: boolean;
    comment?: string;
    note?: string;
}

const getFallbackProjects = (state?: CreateShiftLocationState): PIdNameModel[] => {
    if (state?.projects?.length) {
        return state.projects;
    }

    return state?.projectId
        ? [{ id: state.projectId, name: state.projectName ?? '', isDefault: false }]
        : [];
};

const buildShift = (values: CreateForm, shiftType: TiminatorType, projectId: number): Shift => ({
    id: 0,
    employeeId: values.employee?.id ?? 0,
    date: values.date.toString(),
    shiftType,
    remote: false,
    isOvertime: values.isOvertime,
    putIntoTimeBank: values.putIntoTimeBank,
    billable: false,
    approved: false,
    hours: values.hours,
    enteredHoursString: values.hours.toString(),
    comment: values.comment ?? '',
    note: values.note ?? '',
    recurringPeriod: 0,
    project: { id: projectId } as Shift['project'],
    tags: values.tags
});

const buildAbsence = (values: CreateForm, absenceType: TiminatorAbsenceType): Absence => ({
    id: 0,
    employeeId: values.employee?.id ?? 0,
    date: values.date.toString(),
    hours: values.hours,
    enteredHoursString: values.hours.toString(),
    comment: values.comment ?? '',
    note: values.note ?? '',
    absenceType,
    behavior: absenceType.behavior,
    approved: false,
    takeFromTimeBank: values.takeFromTimeBank,
});

const isLimitToPlannedHoursAbsence = (shiftType: TimeShiftTypeDropdownItem | null): boolean =>
    shiftType?.kind === 'absence'
    && (shiftType.type as TiminatorAbsenceType).behavior === AbsenceBehavior.LimitToPlannedHours;

export default function CreateShiftPage() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector((selector) => selector.profile);
    const state = location.state as CreateShiftLocationState | undefined;
    const today = useMemo(() => moment(), []);
    const returnPath = NAVIGATION_ROUTES.TIMESHEET;
    const canViewSchedule = canSeeTimesheetPage(profile);
    const canManageEntries = canManageTimesheetItems(profile);
    const canManageOldEntries = canManageOldTimesheetItems(profile);
    const canEditInternalNote = canManageTimesheetInternalNote(profile);
    const minEditableDate = useMemo(
        () => canManageOldEntries
            ? today.clone().add(-365, 'days').startOf('day')
            : today.clone().startOf('month'),
        [canManageOldEntries, today]
    );
    const maxEditableDate = useMemo(
        () => today.clone().endOf('month'),
        [today]
    );
    const isAllowedDate = (date?: string): boolean => {
        const parsedDate = moment(date, 'YYYY-MM-DD', true);

        return parsedDate.isValid()
            && !parsedDate.isBefore(minEditableDate, 'day')
            && !parsedDate.isAfter(maxEditableDate, 'day');
    };

    const employees = useAppSelector((selector) => selector.rEmployees);
    const shiftTypes = useAppSelector((selector) => selector.types.shifts.items as TiminatorType[]);
    const absenceTypes = useAppSelector((selector) => selector.types.absences.items as TiminatorAbsenceType[]);
    const tagTypes = useAppSelector((selector) => selector.types.tags.items as TiminatorTagType[]);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [isDayOff, setIsDayOff] = useState(false);
    const [resolvedSelfEmployeeId, setResolvedSelfEmployeeId] = useState<number | null>(state?.employeeId ?? profile.id ?? null);
    const [isResolvingSelfEmployee, setIsResolvingSelfEmployee] = useState(false);
    const normalizeValue = (value?: string | null) => value?.trim().toLowerCase() ?? '';
    const fallbackProjects = useMemo(() => getFallbackProjects(state), [state?.projectId, state?.projectName, state?.projects]);
    const createFallbackEmployee = (employeeId: number): Employee => ({
        id: employeeId,
        firstName: profile.name,
        lastName: '',
        email: profile.email,
        phoneNumber: profile.phoneNumber,
        position: profile.position,
        isActive: true,
        admin: false,
        canEditRoles: false,
        canEditProjects: false,
        canEditAdminLabel: false,
        roles: [],
        projects: fallbackProjects,
    });

    useEffect(() => {
        if (!canViewSchedule) {
            navigate(NAVIGATION_ROUTES.MAIN, { replace: true });
            return;
        }

        if (employees.length === 0 && canManageEntries) {
            dispatch(getAllEmployees());
        }
    }, [canManageEntries, canViewSchedule, dispatch, employees.length, navigate]);

    useEffect(() => {
        if (shiftTypes.length === 0) {
            dispatch(getAllShiftsTypes());
        }
    }, [dispatch, shiftTypes.length]);

    useEffect(() => {
        if (absenceTypes.length === 0) {
            dispatch(getAllAbsencesTypes());
        }
    }, [absenceTypes.length, dispatch]);

    useEffect(() => {
        if (tagTypes.length === 0) {
            dispatch(getAllTagsTypes());
        }
    }, [dispatch, tagTypes.length]);

    useEffect(() => {
        if (!canViewSchedule || canManageEntries || state?.employeeId) {
            return;
        }

        if (profile.id) {
            setResolvedSelfEmployeeId(profile.id);
            return;
        }

        let active = true;

        const resolveSelfEmployee = async () => {
            setIsResolvingSelfEmployee(true);
            const normalizedProfileName = normalizeValue(profile.name);
            const normalizedProfileEmail = normalizeValue(profile.email);
            let availableEmployees: REmployees[] = employees;

            if (availableEmployees.length === 0) {
                try {
                    availableEmployees = await dispatch(getAllEmployees()).unwrap();
                } catch (error) {
                    console.error(error);
                    if (active) {
                        setResolvedSelfEmployeeId(null);
                        setIsResolvingSelfEmployee(false);
                    }
                    return;
                }
            }

            const matchedByName = availableEmployees.find((employee) => normalizeValue(employee.name) === normalizedProfileName);
            if (matchedByName) {
                if (active) {
                    setResolvedSelfEmployeeId(matchedByName.id);
                    setIsResolvingSelfEmployee(false);
                }
                return;
            }

            for (const employee of availableEmployees) {
                try {
                    const fullEmployee = await dispatch(getEmployee(employee.id)).unwrap();
                    if (normalizeValue(fullEmployee.email) === normalizedProfileEmail) {
                        if (active) {
                            setSelectedEmployee(fullEmployee);
                            setResolvedSelfEmployeeId(fullEmployee.id);
                            setIsResolvingSelfEmployee(false);
                        }
                        return;
                    }
                } catch (error) {
                    console.error(error);
                }
            }

            if (active) {
                setResolvedSelfEmployeeId(null);
                setIsResolvingSelfEmployee(false);
            }
        };

        resolveSelfEmployee();

        return () => {
            active = false;
        };
    }, [canManageEntries, canViewSchedule, dispatch, employees, profile.email, profile.id, profile.name, state?.employeeId]);

    const defaultEmployeeId = useMemo(() => {
        const fixedEmployeeId = state?.employeeId ?? resolvedSelfEmployeeId ?? undefined;
        if (fixedEmployeeId && employees.find((e) => e.id === fixedEmployeeId)) {
            return fixedEmployeeId;
        }
        if (!canManageEntries) {
            return fixedEmployeeId;
        }
        return employees[0]?.id;
    }, [canManageEntries, employees, resolvedSelfEmployeeId, state?.employeeId]);

    const employeeDropdownInitialValue = useMemo(() => {
        const fixedEmployeeId = state?.employeeId ?? resolvedSelfEmployeeId ?? undefined;
        if (fixedEmployeeId) {
            return employees.find((employee) => employee.id === fixedEmployeeId) ?? (selectedEmployee
                ? { id: selectedEmployee.id, name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`, isDefault: false }
                : undefined);
        }

        if (selectedEmployee) {
            return {
                id: selectedEmployee.id,
                name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
                isDefault: false,
            };
        }

        return employees[0];
    }, [employees, resolvedSelfEmployeeId, selectedEmployee, state?.employeeId]);

    const loadEmployeeData = async (employeeId: number): Promise<boolean> => {
        if (!employeeId) {
            return false;
        }
        if (selectedEmployee?.id === employeeId) {
            return true;
        }
        try {
            const employee = await dispatch(getEmployee(employeeId)).unwrap();
            setSelectedEmployee(employee);
        } catch (error) {
            if (canManageEntries) {
                throw error;
            }
            setSelectedEmployee(createFallbackEmployee(employeeId));
        }
        return true;
    };

    useEffect(() => {
        if (!canManageEntries && !defaultEmployeeId && !isResolvingSelfEmployee) {
            navigate(returnPath, { replace: true });
            return;
        }

        if (!defaultEmployeeId) {
            return;
        }
        loadEmployeeData(defaultEmployeeId);
    }, [canManageEntries, defaultEmployeeId, isResolvingSelfEmployee, navigate, returnPath]);

    const timeShiftTypes = useMemo<TimeShiftTypeDropdownItem[]>(() => [
        ...shiftTypes.map((type) => ({
            id: `shift-${type.id}`,
            name: type.name,
            kind: 'shift' as const,
            type,
        })),
        ...absenceTypes.map((type) => ({
            id: `absence-${type.id}`,
            name: type.name,
            kind: 'absence' as const,
            type,
        })),
    ], [absenceTypes, shiftTypes]);

    // On a day off for the selected employee only shift (project) entries are allowed, mirroring the Slack shift dialog.
    const availableTimeShiftTypes = useMemo<TimeShiftTypeDropdownItem[]>(
        () => isDayOff ? timeShiftTypes.filter((item) => item.kind === 'shift') : timeShiftTypes,
        [isDayOff, timeShiftTypes]
    );

    const tagOptions = useMemo<PIdNameModel[]>(() => tagTypes.map((tag) => ({
        id: tag.id,
        name: tag.name,
        isDefault: tag.default,
    })), [tagTypes]);

    const copyPrefill = state?.copy;
    const initialValuesRef = useRef<CreateForm | null>(null);
    const initialValues = useMemo<CreateForm | null>(() => {
        if (initialValuesRef.current) {
            return initialValuesRef.current;
        }
        if (!selectedEmployee || shiftTypes.length === 0) {
            return null;
        }
        // When copying an absence we need the absence types resolved to preselect the correct type.
        if (copyPrefill?.kind === 'absence' && absenceTypes.length === 0) {
            return null;
        }
        const requestedDate = state?.date?.trim()
            ? moment(state.date, 'YYYY-MM-DD', true)
            : today.clone();
        const normalizedDate = requestedDate.isValid() ? requestedDate : today.clone();
        const date = normalizedDate.isBefore(minEditableDate, 'day')
            ? minEditableDate.format('YYYY-MM-DD')
            : normalizedDate.isAfter(maxEditableDate, 'day')
                ? maxEditableDate.format('YYYY-MM-DD')
                : normalizedDate.format('YYYY-MM-DD');

        const defaultShiftType: TimeShiftTypeDropdownItem | null = shiftTypes[0]
            ? {
                id: `shift-${shiftTypes[0].id}`,
                name: shiftTypes[0].name,
                kind: 'shift',
                type: shiftTypes[0],
            }
            : null;

        let shiftType = defaultShiftType;
        if (copyPrefill?.kind === 'shift') {
            const matched = shiftTypes.find((type) => type.id === copyPrefill.typeId);
            if (matched) {
                shiftType = { id: `shift-${matched.id}`, name: matched.name, kind: 'shift', type: matched };
            }
        } else if (copyPrefill?.kind === 'absence') {
            const matched = absenceTypes.find((type) => type.id === copyPrefill.typeId);
            if (matched) {
                shiftType = { id: `absence-${matched.id}`, name: matched.name, kind: 'absence', type: matched };
            }
        }

        const isAbsenceCopy = shiftType?.kind === 'absence';
        const copiedProject = copyPrefill?.kind === 'shift' && state?.projectId
            ? selectedEmployee.projects.find((project) => project.id === state.projectId)
            : undefined;
        const copiedTags = !isAbsenceCopy && copyPrefill?.tags?.length
            ? copyPrefill.tags.map((tag) => tagOptions.find((option) => option.id === tag.id) ?? tag)
            : [];

        const value: CreateForm = {
            employee: employeeDropdownInitialValue ?? null,
            date,
            shiftType,
            project: isAbsenceCopy ? null : (copiedProject ?? selectedEmployee.projects[0] ?? null),
            tags: copiedTags,
            hours: copyPrefill ? copyPrefill.hours : 8,
            plannedHours: null,
            isOvertime: !isAbsenceCopy && (copyPrefill?.isOvertime ?? false),
            putIntoTimeBank: false,
            takeFromTimeBank: false,
            comment: copyPrefill?.note ?? '',
            note: copyPrefill?.internalNote ?? '',
        };
        initialValuesRef.current = value;
        return value;
    }, [absenceTypes, copyPrefill, employeeDropdownInitialValue, maxEditableDate, minEditableDate, selectedEmployee, shiftTypes, state?.date, state?.projectId, tagOptions, today]);

    const validationScheme: Yup.Schema<CreateForm> = useMemo(() => Yup.object().shape({
        employee: Yup.mixed<REmployees>()
            .nullable()
            .required()
            .test(
                'change-employee',
                'Done',
                async (value: REmployees | null) => value ? await loadEmployeeData(value.id) : false
            ),
        shiftType: Yup.mixed<TimeShiftTypeDropdownItem>().nullable().required().test(
            'shift-type-not-absence-on-day-off',
            'Only shift entries are allowed on a day off',
            (value) => !isDayOff || value?.kind !== 'absence'
        ),
        project: Yup.mixed<PIdNameModel>().nullable().test(
            'project-required-for-shift',
            'Project is required',
            (value, context) => (context.parent as CreateForm).shiftType?.kind === 'absence' || Boolean(value)
        ).defined(),
        tags: Yup.array().of(Yup.mixed<PIdNameModel>().required()).defined(),
        date: Yup.string().required().test('allowed-date', 'Invalid date', (value) => isAllowedDate(value)),
        hours: Yup.number()
            .required()
            .min(0.5)
            .max(24)
            .test(
                'limit-to-planned-hours',
                'Hours must be equal to planned hours',
                (value, context) => {
                    const form = context.parent as CreateForm;
                    if (!isLimitToPlannedHoursAbsence(form.shiftType)) {
                        return true;
                    }

                    if (typeof form.plannedHours !== 'number') {
                        return true;
                    }

                    return typeof value === 'number' && value === form.plannedHours;
                }
            )
            .test(
                'time-bank-whole-hours',
                'Time bank hours must be whole hours',
                (value, context) => {
                    const form = context.parent as CreateForm;

                    if (!form.putIntoTimeBank && !form.takeFromTimeBank) {
                        return true;
                    }

                    return typeof value === 'number' && Number.isInteger(value);
                }
            ),
        plannedHours: Yup.number().nullable().default(null),
        isOvertime: Yup.boolean().required(),
        putIntoTimeBank: Yup.boolean().required(),
        takeFromTimeBank: Yup.boolean().required(),
        comment: Yup.string().optional(),
        note: Yup.string().optional(),
    }), [isDayOff, maxEditableDate, minEditableDate, selectedEmployee]);

    const add = async (values: CreateForm) => {
        if (!selectedEmployee) {
            return;
        }
        if (!values.employee || !values.shiftType) {
            return;
        }
        const selectedTimeShiftType = values.shiftType;
        if (!canManageEntries && values.employee.id !== selectedEmployee.id) {
            return;
        }
        if (!isAllowedDate(values.date)) {
            return;
        }
        if (selectedTimeShiftType.kind === 'absence') {
            await dispatch(createAbsence(buildAbsence(values, selectedTimeShiftType.type as TiminatorAbsenceType))).unwrap();
            navigate(returnPath);
            return;
        }

        if (!values.project) {
            return;
        }

        const projectId = values.project.id;
        if (!projectId) {
            return;
        }

        const shiftType = shiftTypes.find((x) => x.id === selectedTimeShiftType.type.id);
        if (!shiftType) {
            return;
        }

        await dispatch(createShift(buildShift(values, shiftType, projectId))).unwrap();
        navigate(returnPath);
    };

    const employeeSelectionDisabled = !canManageEntries || (state?.lockEmployee ?? false) || employees.length < 2;
    const employeeDropdownItems = canManageEntries
        ? employees
        : employeeDropdownInitialValue
            ? [employeeDropdownInitialValue]
            : [];

    if (!canViewSchedule || isResolvingSelfEmployee || !initialValues) {
        return null;
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            validateOnBlur={true}
            validateOnChange={true}
            enableReinitialize
            onSubmit={add}>
            {({ values, setFieldValue, setFieldTouched }) => {
                const isAbsence = values.shiftType?.kind === 'absence';
                const limitToPlannedHours = isLimitToPlannedHoursAbsence(values.shiftType);
                const useTimeBankHours = values.putIntoTimeBank || values.takeFromTimeBank;
                const typeLabel = intl.formatMessage({ id: isAbsence ? Lx.General.ABSENCE_TYPE : Lx.General.SHIFT_TYPE });
                const hoursLabel = intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_HOURS });
                const touchHoursForTimeBankValidation = () => {
                    setFieldTouched('hours', true, true);
                };

                return (
                    <Form>
                        <CreateShiftFormEffects
                            selectedEmployee={selectedEmployee}
                            loadEmployeeData={loadEmployeeData}
                            desiredProjectId={copyPrefill?.kind === 'shift' ? state?.projectId : undefined}
                            shiftTypes={shiftTypes}
                            isDayOff={isDayOff}
                            onDayOffChange={setIsDayOff}
                        />
                        <BaseForm
                            contentGap={24}
                            formType='create'
                            formHeader={isAbsence ? Lx.Schedule.CREATE_NEW_TIMESHIFT_ABSENCE : Lx.Schedule.CREATE_NEW_TIMESHIFT_SHIFT}
                            savingTitle={intl.formatMessage({ id: isAbsence ? Lx.General.ABSENCE : Lx.General.SHIFT })}
                            onClose={() => navigate(returnPath)}
                            onAfterSave={() => navigate(returnPath)}
                            allowPristineSubmit
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
                                <DropdownField label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                                    name={'employee'}
                                    items={employeeDropdownItems}
                                    required
                                    placeholder={intl.formatMessage({ id: Lx.General.CHOOSE_EMPLOYEE })}
                                    disabled={employeeSelectionDisabled}/>
                                {
                                    selectedEmployee && (
                                        <>
                                            <Box
                                                display={'flex'}
                                                flexDirection={'row'}
                                                alignItems={'center'}
                                                gap={3}
                                                justifyContent={'stretch'}
                                                sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
                                                <FormDateSelector
                                                    minDate={minEditableDate}
                                                    maxDate={maxEditableDate}
                                                    label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_DATE })}
                                                    name={'date'}
                                                    required />
                                                <DropdownField label={typeLabel}
                                                    name={'shiftType'}
                                                    placeholder={typeLabel}
	                                            items={availableTimeShiftTypes}
                                                    onChange={(item) => {
                                                        if (item?.kind === 'shift') {
                                                            setFieldValue('project', selectedEmployee.projects[0] ?? null, false);
                                                        }
                                                    }}
                                                    required/>
                                            </Box>
                                            <Box
                                                display={'flex'}
                                                flexDirection={'row'}
                                                alignItems={'flex-start'}
                                                gap={3}
                                                sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
                                                <DropdownField label={intl.formatMessage({ id: Lx.General.PROJECT })}
                                                    name={'project'}
                                                    items={selectedEmployee.projects}
                                                    required={values.shiftType?.kind !== 'absence'}
                                                    placeholder={intl.formatMessage({ id: Lx.General.PROJECT })}
                                                    disabled={values.shiftType?.kind === 'absence'}/>
                                                {limitToPlannedHours ? (
                                                    <PlannedHoursOnlyNumberInput
                                                        label={hoursLabel}
                                                        placeholder={hoursLabel}
                                                        required />
                                                ) : (
                                                    <FormNumberInput
                                                        max={24}
                                                        min={useTimeBankHours ? 1 : 0.5}
                                                        step={useTimeBankHours ? 1 : 0.5}
                                                        label={hoursLabel} name={'hours'}
                                                        placeholder={hoursLabel} required/>
                                                )}
                                            </Box>
                                            {values.shiftType?.kind !== 'absence' && (
                                                <FormAutocomplete
                                                    placeHolder={intl.formatMessage({ id: Lx.General.START_TYPING })}
                                                    label={intl.formatMessage({ id: Lx.General.TAGS })}
                                                    name='tags'
                                                    items={tagOptions}
                                                    selectedItems={values.tags}
                                                />
                                            )}
                                            {values.shiftType?.kind !== 'absence' && (
                                                <>
                                                    <FormCheckbox
                                                        name='isOvertime'
                                                        label={intl.formatMessage({ id: Lx.Schedule.IS_OVERTIME })}
                                                        onChange={(event) => {
                                                            setFieldValue('isOvertime', event.target.checked);
                                                            if (event.target.checked) {
                                                                setFieldValue('putIntoTimeBank', false);
                                                            }
                                                        }}
                                                        style={{ marginBottom: 0 }} />
                                                    <FormCheckbox
                                                        name='putIntoTimeBank'
                                                        label={intl.formatMessage({ id: Lx.Schedule.ADD_TO_TIME_BANK })}
                                                        onChange={(event) => {
                                                            touchHoursForTimeBankValidation();
                                                            setFieldValue('putIntoTimeBank', event.target.checked, true);
                                                            if (event.target.checked) {
                                                                setFieldValue('isOvertime', false, false);
                                                            }
                                                        }}
                                                        style={{ marginBottom: 0 }} />
                                                </>
                                            )}
                                            {values.shiftType?.kind === 'absence' && !limitToPlannedHours && (
                                                <FormCheckbox
                                                    name='takeFromTimeBank'
                                                    label={intl.formatMessage({ id: Lx.Schedule.USE_FROM_TIME_BANK })}
                                                    onChange={(event) => {
                                                        touchHoursForTimeBankValidation();
                                                        setFieldValue('takeFromTimeBank', event.target.checked, true);
                                                    }}
                                                    style={{ marginBottom: 0 }} />
                                            )}
                                            <FormTextarea label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_NOTE })} name={'comment'}
                                                placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_NOTE })}
                                                resize={'vertical'}
                                                multiple={true}/>
                                            {canEditInternalNote && (
                                                <FormTextarea label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_INTERNAL_NOTE })} name={'note'}
                                                    placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_INTERNAL_NOTE })}
                                                    resize={'vertical'}
                                                    multiple={true}/>
                                            )}
                                        </>
                                    )
                                }
                            </div>
                        </BaseForm>
                    </Form>
                );
            }}
        </Formik>
    );
}

const CreateShiftFormEffects: React.FC<{
    selectedEmployee: Employee | null;
    loadEmployeeData: (employeeId: number) => Promise<boolean>;
    desiredProjectId?: number;
    shiftTypes: TiminatorType[];
    isDayOff: boolean;
    onDayOffChange: (isDayOff: boolean) => void;
}> = ({ selectedEmployee, loadEmployeeData, desiredProjectId, shiftTypes, isDayOff, onDayOffChange }) => {
    const { values, setFieldValue } = useFormikContext<CreateForm>();
    // Preselect a specific project (e.g. when copying a shift) only once, then fall back to defaults.
    const desiredProjectIdRef = useRef<number | undefined>(desiredProjectId);

    useEffect(() => {
        if (!values.employee?.id) {
            return;
        }
        loadEmployeeData(values.employee.id);
    }, [values.employee]);

    // Mirrors the Slack shift dialog: on a day off for the employee only shift (project) entries are allowed.
    useEffect(() => {
        const employeeId = selectedEmployee?.id;
        const parsedDate = moment(values.date, 'YYYY-MM-DD', true);
        if (!employeeId || !parsedDate.isValid()) {
            onDayOffChange(false);
            return;
        }

        let active = true;

        const checkDayOff = async () => {
            try {
                const calendarResponse = await HttpClient.workcaledarApi.getWorkCalendarIdByEmployeeId<number>(employeeId);
                const workCalendarId = calendarResponse.data;
                if (!workCalendarId) {
                    if (active) {
                        onDayOffChange(false);
                    }
                    return;
                }
                const dayResponse = await HttpClient.workcaledarApi.getDay<Day>(
                    workCalendarId,
                    parsedDate.year(),
                    parsedDate.month() + 1,
                    parsedDate.date()
                );
                if (active) {
                    onDayOffChange(Boolean(dayResponse.data?.isHoliday));
                }
            } catch (error) {
                console.error(error);
                if (active) {
                    onDayOffChange(false);
                }
            }
        };

        checkDayOff();

        return () => {
            active = false;
        };
    }, [selectedEmployee?.id, values.date, onDayOffChange]);

    useEffect(() => {
        if (!isDayOff || values.shiftType?.kind !== 'absence') {
            return;
        }
        const fallbackShiftType = shiftTypes[0];
        if (fallbackShiftType) {
            setFieldValue('shiftType', { id: `shift-${fallbackShiftType.id}`, name: fallbackShiftType.name, kind: 'shift', type: fallbackShiftType }, false);
        }
    }, [isDayOff, values.shiftType?.kind, shiftTypes, setFieldValue]);

    useEffect(() => {
        if (!selectedEmployee) {
            return;
        }
        if (desiredProjectIdRef.current != null) {
            const desired = selectedEmployee.projects.find((project) => project.id === desiredProjectIdRef.current);
            desiredProjectIdRef.current = undefined;
            if (desired) {
                setFieldValue('project', desired, false);
                return;
            }
        }
        setFieldValue('project', selectedEmployee.projects[0] ?? null, false);
    }, [selectedEmployee, setFieldValue]);

    useEffect(() => {
        if (!selectedEmployee) {
            return;
        }
        if (values.shiftType?.kind === 'absence') {
            setFieldValue('project', null, false);
            setFieldValue('isOvertime', false, false);
            setFieldValue('putIntoTimeBank', false, false);
            if (isLimitToPlannedHoursAbsence(values.shiftType)) {
                setFieldValue('takeFromTimeBank', false, false);
            } else {
                setFieldValue('plannedHours', null, false);
            }
            return;
        }
        setFieldValue('takeFromTimeBank', false, false);
        setFieldValue('plannedHours', null, false);
        if (!values.project) {
            setFieldValue('project', selectedEmployee.projects[0] ?? null, false);
        }
    }, [selectedEmployee, setFieldValue, values.project, values.shiftType]);

    return null;
};

const PlannedHoursOnlyNumberInput: React.FC<{
    label: string;
    placeholder: string;
    required?: boolean;
}> = ({ label, placeholder, required }) => {
    const { values, setFieldValue } = useFormikContext<CreateForm>();
    const [plannedHours, setPlannedHours] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!values.employee?.id || !values.date) {
            return;
        }

        let active = true;
        setPlannedHours(null);
        setFieldValue('plannedHours', null, false);
        setIsLoading(true);

        HttpClient.shiftsApi.fetchPlannedHours({
            employeeId: values.employee.id,
            startDate: values.date,
            endDate: values.date,
        })
            .then((hours) => {
                if (!active) {
                    return;
                }

                setPlannedHours(hours);
                setFieldValue('plannedHours', hours, false);
                setFieldValue('hours', hours, true);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                console.error(error);
                setPlannedHours(null);
                setFieldValue('plannedHours', null, false);
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [setFieldValue, values.date, values.employee?.id]);

    return (
        <FormNumberInput
            max={plannedHours ?? undefined}
            min={plannedHours ?? undefined}
            step={0.5}
            label={label}
            name='hours'
            placeholder={placeholder}
            required={required}
            disabled={isLoading}/>
    );
};
